import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { reset_link, message }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
      if (data.reset_link) toast.success("Reset link generated below.");
      else toast.error(data.message || "No account found");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(result.reset_link);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12" data-testid="forgot-page">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-black/5"
        data-testid="forgot-form"
      >
        <h1 className="font-display text-3xl font-bold uppercase" style={{ color: "#13A699" }}>Forgot Password</h1>
        <p className="mt-1 text-sm" style={{ color: "#13A699" }}>
          Enter your email — we'll generate a reset link instantly.
        </p>

        {result && result.reset_link ? (
          <div className="mt-6 rounded-2xl p-4 bg-[#FFF7ED] border-2 border-[#FFD708]" data-testid="forgot-link-box">
            <p className="text-sm font-bold" style={{ color: "#13A699" }}>Your one-time reset link (valid 1 hour):</p>
            <div className="mt-2 rounded-lg bg-white border border-black/10 p-2 break-all text-xs" style={{ color: "#13A699" }} data-testid="reset-link-text">
              {result.reset_link}
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <a
                href={result.reset_link}
                className="btn-yellow text-xs px-4 py-2 inline-flex items-center"
                data-testid="open-reset-link-btn"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open
              </a>
              <button
                type="button"
                onClick={copyLink}
                className="btn-outline-teal text-xs px-4 py-2 inline-flex items-center"
                data-testid="copy-reset-link-btn"
              >
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy Link
              </button>
            </div>
            <p className="text-[11px] mt-3 opacity-80" style={{ color: "#13A699" }}>
              Tip: this link is sensitive — share only with the account owner.
            </p>
          </div>
        ) : result && !result.reset_link ? (
          <div className="mt-6 rounded-2xl p-4 bg-[#FFF7ED] border border-red-300" data-testid="forgot-not-found">
            <p className="text-sm" style={{ color: "#13A699" }}>{result.message || "No account found for this email."}</p>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="btn-outline-teal text-xs px-3 py-1.5 mt-2"
              data-testid="forgot-try-again-btn"
            >
              Try another email
            </button>
          </div>
        ) : (
          <>
            <label className="block mt-5 text-sm font-semibold" style={{ color: "#13A699" }}>Email</label>
            <input
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:ring-2 focus:ring-[#13A699] bg-white text-[#13A699]"
              data-testid="forgot-email-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="btn-yellow w-full mt-6 py-3 disabled:opacity-60"
              data-testid="forgot-submit-btn"
            >
              {submitting ? "Generating..." : "Get Reset Link"}
            </button>
          </>
        )}

        <div className="mt-4 text-sm text-center" style={{ color: "#13A699" }}>
          <Link to="/login" className="font-bold">Back to Login</Link>
        </div>
      </form>
    </div>
  );
}
